import React, { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";

const DynamicSections = ({
  label,
  placeholderHeading = "Add type",
  placeholderDescription = "Add description",
  enableAddNew = false,
  value,
  onChange,
}) => {
  const [sections, setSections] = useState(() => [
    { type: "", descriptions: [""] },
  ]);
  useEffect(() => {
    setSections(
      Array.isArray(value) ? value : [{ type: "", descriptions: [""] }]
    );
  }, [value]);
  const handleAddSection = () => {
    setSections((prev) => [...prev, { type: "", descriptions: [""] }]);
  };

  const handleTypeChange = (index, newType) => {
    const updated = [...sections];
    updated[index].type = newType;
    setSections(updated);
    onChange?.(updated);
  };

  const handleDescriptionChange = (sectionIndex, descIndex, newValue) => {
    const updated = [...sections];
    updated[sectionIndex].descriptions[descIndex] = newValue;
    setSections(updated);
    onChange?.(updated);
  };

  const handleAddDescription = (sectionIndex) => {
    const updated = [...sections];
    updated[sectionIndex].descriptions.push("");
    setSections(updated);
    onChange?.(updated);
  };

  const handleRemoveDescription = (sectionIndex, descIndex) => {
    const updated = [...sections];
    updated[sectionIndex].descriptions = updated[
      sectionIndex
    ].descriptions.filter((_, i) => i !== descIndex);
    if (updated[sectionIndex].descriptions.length === 0) {
      updated[sectionIndex].descriptions = [""];
    }
    setSections(updated);
    onChange?.(updated);
  };

  const handleRemoveSection = (index) => {
    const updated = sections?.filter((_, i) => i !== index);
    setSections(updated.length ? updated : [{ type: "", descriptions: [""] }]);
    onChange?.(updated);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Label */}
      {label && (
        <label className="block text-base sm:text-lg font-semibold text-text-primary font-poppins">
          {label}
        </label>
      )}

      {/* Sections */}
      <div className="space-y-2">
        {sections?.map((section, sectionIndex) => (
          <div
            key={sectionIndex}
            className="border border-border rounded-lg p-3"
          >
            {/* Type Input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={section.type}
                onChange={(e) => handleTypeChange(sectionIndex, e.target.value)}
                placeholder={placeholderHeading}
                className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-sm"
              />
              {sections?.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveSection(sectionIndex)}
                  className="p-2 text-status-danger hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove section"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>

            {/* Descriptions */}
            <div className={`space-y-3 flex flex-col w-full`}>
              {section.descriptions.map((desc, descIndex) => (
                <div key={descIndex} className="flex gap-y-2 gap-x-1">
                  {section.descriptions.length > 1 && (
                    <div className="w-2 h-2 bg-text-muted rounded-full my-auto mx-1"></div>
                  )}
                  <input
                    type="text"
                    value={desc}
                    onChange={(e) =>
                      handleDescriptionChange(
                        sectionIndex,
                        descIndex,
                        e.target.value
                      )
                    }
                    placeholder={placeholderDescription}
                    className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none outline-none focus:ring-1 focus:ring-primary text-sm"
                  />
                  {section.descriptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveDescription(sectionIndex, descIndex)
                      }
                      className="p-2 text-text-muted hover:text-status-danger hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove description"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}

              {/* Add Description Button */}
              <button
                type="button"
                onClick={() => handleAddDescription(sectionIndex)}
                className="flex self-end items-center gap-2 text-primary hover:text-btn-primary font-medium text-sm mt-2 transition-colors"
              >
                <Plus size={16} /> Add Description
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add New Section Button */}
      {enableAddNew && (
        <button
          type="button"
          onClick={handleAddSection}
          className="flex items-center justify-center gap-2 px-4 py-3 text-primary hover:text-btn-primary font-medium text-sm border border-primary rounded-lg hover:bg-blue-50 transition-all"
        >
          <Plus size={16} /> Add Section
        </button>
      )}

      {/* Preview */}
      {sections?.some(
        (s) => s.type.trim() || s.descriptions.some((d) => d.trim())
        // Check if at least one section has a type or at least one non-empty description.
      ) && (
        <div className="mt-4 p-4 bg-background-DEFAULT rounded-lg border border-border">
          <h3 className="text-xs font-semibold text-text-primary/80 uppercase mb-3 font-poppins">
            Preview
          </h3>
          <ul className="space-y-3">
            {sections
              ?.filter(
                (s) => s.type.trim() || s.descriptions.some((d) => d.trim())
              )
              .map((section, i) => (
                <li key={i} className="text-sm">
                  {section.type && (
                    <span className="font-semibold text-text-primary uppercase block mb-2 font-poppins">
                      {section.type}
                    </span>
                  )}
                  {section.descriptions.some((d) => d.trim()) && (
                    <ul className="list-disc pl-5 space-y-1 text-text-secondary">
                      {section.descriptions
                        .filter((d) => d.trim())
                        .map((desc, j) => (
                          <li key={j}>{desc}</li>
                        ))}
                    </ul>
                  )}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DynamicSections;
